import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Calendar, Sparkles, Wrench, Bug, RefreshCw } from "lucide-react";
import Navbar from "../components/Navbar";
import SEO from "../components/SEO";
import { API } from "../App";
import axios from "axios";

const CATEGORY_CONFIG = {
  feature: {
    label: "New Feature",
    icon: Sparkles,
    color: "text-green-400",
    bgColor: "bg-green-500/10",
    borderColor: "border-green-500/30"
  },
  improvement: {
    label: "Improvement",
    icon: RefreshCw,
    color: "text-blue-400",
    bgColor: "bg-blue-500/10",
    borderColor: "border-blue-500/30"
  },
  fix: {
    label: "Bug Fix",
    icon: Bug,
    color: "text-orange-400",
    bgColor: "bg-orange-500/10",
    borderColor: "border-orange-500/30"
  },
  update: {
    label: "Update",
    icon: Wrench,
    color: "text-purple-400",
    bgColor: "bg-purple-500/10",
    borderColor: "border-purple-500/30"
  }
};

const UpdateCard = ({ update, index }) => {
  const config = CATEGORY_CONFIG[update.category] || CATEGORY_CONFIG.update;
  const Icon = config.icon;
  
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className={`bg-hsl-surface border ${config.borderColor} rounded-xl p-5 hover:border-opacity-60 transition-colors`}
    >
      <div className="flex items-start gap-4">
        <div className={`p-2.5 rounded-lg ${config.bgColor}`}>
          <Icon className={`w-5 h-5 ${config.color}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <span className={`text-xs font-semibold px-2 py-0.5 rounded ${config.bgColor} ${config.color}`}>
              {config.label}
            </span>
            <span className="text-zinc-500 text-xs flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {formatDate(update.created_at)}
            </span>
          </div>
          <h3 className="text-white font-semibold text-lg mb-1">{update.title}</h3>
          <p className="text-zinc-400 text-sm leading-relaxed">{update.description}</p>
        </div>
      </div>
    </motion.div>
  );
};

const UpdatesPage = () => {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUpdates = async () => {
      try {
        const response = await axios.get(`${API}/site-updates`);
        setUpdates(response.data);
      } catch (error) {
        console.warn("Failed to fetch updates:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchUpdates();
  }, []);

  return (
    <div className="min-h-screen bg-hsl-bg">
      <SEO 
        title="Site Updates | HSL.bet"
        description="See the latest updates, new features, and improvements to HSL.bet"
      />
      <Navbar />
      
      {/* Hero */}
      <div className="pt-24 pb-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-heading font-black uppercase text-white mb-4"
          >
            Site Updates
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-zinc-400 text-lg max-w-2xl mx-auto"
          >
            We're constantly improving HSL.bet with new features and enhancements. 
            Here's what's new.
          </motion.p>
        </div>
      </div>

      {/* Updates List */}
      <div className="max-w-4xl mx-auto px-4 pb-16">
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-hsl-surface rounded-xl p-5 animate-pulse">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-white/10 rounded-lg" />
                  <div className="flex-1">
                    <div className="h-4 w-24 bg-white/10 rounded mb-3" />
                    <div className="h-5 w-3/4 bg-white/10 rounded mb-2" />
                    <div className="h-4 w-full bg-white/10 rounded" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : updates.length > 0 ? (
          <div className="space-y-4">
            {updates.map((update, index) => (
              <UpdateCard key={update.id} update={update} index={index} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-hsl-surface rounded-xl border border-white/5">
            <Wrench className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400 text-lg">No updates yet</p>
            <p className="text-zinc-600 text-sm mt-1">Check back soon for the latest improvements</p>
          </div>
        )}
      </div>

      {/* Footer Note */}
      <div className="max-w-4xl mx-auto px-4 pb-16">
        <div className="bg-gradient-to-r from-hsl-primary/10 to-transparent border border-hsl-primary/20 rounded-xl p-6 text-center">
          <p className="text-zinc-400 text-sm">
            Have a feature request or found a bug? 
            <a href="mailto:support@hsl.bet" className="text-hsl-primary hover:underline ml-1">
              Let us know
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default UpdatesPage;
